import axios from 'axios';

/**
 * Advisor Network API (Phase 1: connections).
 * Each call takes an Auth0 access token and hits the platform server's /network routes.
 */

const BASE = `${process.env.REACT_APP_SERVER}network`;

const authCfg = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export async function getConnections(token, status = 'all') {
    const { data } = await axios.get(`${BASE}/connections?status=${status}`, authCfg(token));
    return data.connections || [];
}

export async function getInvitations(token) {
    const { data } = await axios.get(`${BASE}/connections/invitations`, authCfg(token));
    return data.invitations || [];
}

export async function inviteAdvisor(token, { email, message }) {
    const { data } = await axios.post(`${BASE}/connections/invite`, { email, message }, authCfg(token));
    return data.connection;
}

/** Attach an invitation from an email link to the signed-in account (see components/PendingInviteClaimer). */
export async function claimInvitation(token, inviteToken) {
    const { data } = await axios.post(`${BASE}/connections/claim`, { token: inviteToken }, authCfg(token));
    return data;
}

export async function acceptConnection(token, id) {
    const { data } = await axios.post(`${BASE}/connections/${id}/accept`, {}, authCfg(token));
    return data.connection;
}

export async function declineConnection(token, id) {
    const { data } = await axios.post(`${BASE}/connections/${id}/decline`, {}, authCfg(token));
    return data.connection;
}

export async function revokeConnection(token, id) {
    const { data } = await axios.post(`${BASE}/connections/${id}/revoke`, {}, authCfg(token));
    return data.connection;
}

// ---- Referrals (Phase 2) ----

export async function getServiceTypes(token) {
    const { data } = await axios.get(`${BASE}/referrals/service-types`, authCfg(token));
    return data.serviceTypes || [];
}

export async function getReferralCandidates(token) {
    const { data } = await axios.get(`${BASE}/referrals/candidates`, authCfg(token));
    return data.candidates || [];
}

// Preview of what a candidate client actually has to share (per category).
export async function getReferralCandidateData(token, conversationId) {
    const { data } = await axios.get(
        `${BASE}/referrals/candidate-data?conversationId=${encodeURIComponent(conversationId)}`,
        authCfg(token),
    );
    return data || {};
}

export async function getReferrals(token, box = 'all') {
    const { data } = await axios.get(`${BASE}/referrals?box=${box}`, authCfg(token));
    return data.referrals || [];
}

export async function createReferral(token, payload) {
    const { data } = await axios.post(`${BASE}/referrals`, payload, authCfg(token));
    return data.referral;
}

export async function acceptReferral(token, id) {
    const { data } = await axios.post(`${BASE}/referrals/${id}/accept`, {}, authCfg(token));
    return data.referral;
}

export async function declineReferral(token, id, reason) {
    const { data } = await axios.post(`${BASE}/referrals/${id}/decline`, { reason }, authCfg(token));
    return data.referral;
}

export async function updateReferralStatus(token, id, status, note) {
    const { data } = await axios.post(`${BASE}/referrals/${id}/status`, { status, note }, authCfg(token));
    return data.referral;
}

export async function cancelReferral(token, id) {
    const { data } = await axios.post(`${BASE}/referrals/${id}/cancel`, {}, authCfg(token));
    return data.referral;
}

// ---- Public consent (no auth) ----

export async function getConsent(consentToken) {
    const { data } = await axios.get(`${BASE}/referrals/consent/${consentToken}`);
    return data;
}

export async function respondConsent(consentToken, approve) {
    const { data } = await axios.post(`${BASE}/referrals/consent/${consentToken}`, { approve });
    return data;
}
